/**
 * Types for the temporary session snapshot used to restore planner state
 * after an external redirect such as SwissPass OAuth.
 */

import type { PanelSnapTier } from "../../components/ItinerariesPlanner/panelResize/panelResize.types";
import type { SavedListFilterTabId } from "../../tools/savedItineraryDisplay.tools";
import type { ItineraryDTO } from "../../types/dtos/api/itinerary/itinerary.dto";
import type { DateWithMode } from "../../types/Forms";
import type { SearchResultItem } from "../../types/geocode-ui.types";
import type { ItineraryListContentMode } from "../../types/ItineraryPlanner";

/**
 * Current snapshot format version.
 * Bump when the shape of {@link AppSessionSnapshot} changes incompatibly.
 */
export const APP_SESSION_SNAPSHOT_VERSION = 1;

/**
 * Keys contributed to the snapshot.
 * @remarks Well-known keys are "plannerSession" and "mapCamera"; arbitrary
 * strings can be used for additional contributors.
 */
export type AppSessionSnapshotKey = string;

/**
 * Top-level snapshot written to session/local storage.
 */
export interface AppSessionSnapshot {

    /** Snapshot schema version. */
    "version": number;

    /** Epoch milliseconds when the snapshot was saved. */
    "savedAt": number;

    /** Planner search, panel, and itinerary state. */
    "plannerSession"?: PlannerSessionSnapshot;

    /** Map camera state, captured only when a planner session exists. */
    "mapCamera"?: MapCameraSnapshot;
}

/**
 * Capturable planner UI state.
 */
export interface PlannerSessionSnapshot {

    /** Whether the itinerary panel is visible. */
    "visible": boolean;

    /** Current content surface of the itinerary list. */
    "itineraryListContentMode": ItineraryListContentMode;

    /** Active filter tab in saved-itinerary mode. */
    "activeSavedFilterTab": SavedListFilterTabId;

    /** Selected itinerary for details view. */
    "selectedItinerary": ItineraryDTO | null;

    /** Whether the user has chosen to take their car. */
    "ITakeMyCar": boolean;

    /** Car itinerary selected by the user. */
    "ITakeMyCarData": ItineraryDTO | null;

    /** Selected departure geocode, if any. */
    "departureSelectedAddress": SearchResultItem | null;

    /** Selected arrival geocode, if any. */
    "arrivalSelectedAddress": SearchResultItem | null;

    /** Departure search input text. */
    "departureInput": string;

    /** Arrival search input text. */
    "arrivalInput": string;

    /** Planner date with departure/arrival mode. */
    "date": DateWithMode;

    /** Whether the date has been manually edited. */
    "isEditedDate": boolean;

    /** Whether the two search fields are merged into one. */
    "isMerge": boolean;

    /** Current list of itinerary results. */
    "itineraries": ItineraryDTO[];

    /** Active snap tier of the bottom panel. */
    "panelSnapTier": PanelSnapTier;

    /** Last remembered snap tier for the itinerary list. */
    "rememberedItineraryListSnapTier": PanelSnapTier;

    /** Last remembered snap tier for the itinerary details view. */
    "rememberedItineraryDetailsSnapTier": PanelSnapTier;
}

/**
 * Map camera state captured from MapLibre.
 */
export interface MapCameraSnapshot {

    /** Camera center as [longitude, latitude]. */
    "center": [number, number];

    /** Zoom level. */
    "zoom": number;

    /** Bearing in degrees. */
    "bearing": number;

    /** Pitch in degrees. */
    "pitch": number;
}
